import { store } from '../store';
import {
  setLoading,
  setError,
  setTasks,
  addTask,
  updateTask,
  removeTask,
  updateTaskState,
  setRuns,
  addOrUpdateRun,
  setAllRuns,
  appendAllRuns,
} from '../store/slices/scheduledTaskSlice';
import type {
  ScheduledTaskInput,
  ScheduledTaskStatusEvent,
  ScheduledTaskRunEvent,
} from '../types/scheduledTask';
import { relayService } from './relay';
import { i18nService } from './i18n';

type ScheduledTasksApi = NonNullable<NonNullable<typeof window.electron>['scheduledTasks']>;

/**
 * 定时任务的数据源。云端是 window.electron.scheduledTasks（REST），桌面设备是
 * relay 适配器。后者没有的几项——清空历史、计数、状态推送、执行推送——声明成可选，
 * 调用处已经按「没有就降级」处理。
 */
type OptionalOnDesktop = 'clearRunHistory' | 'countRuns' | 'onStatusUpdate' | 'onRunUpdate';
type ScheduledTasksSource =
  Omit<ScheduledTasksApi, OptionalOnDesktop> & Partial<Pick<ScheduledTasksApi, OptionalOnDesktop>>;

/** 桌面端设备上不支持的操作统一用它回绝，UI 按 success:false 走原有报错路径。 */
const desktopUnsupported = async () => ({
  success: false as const,
  error: i18nService.t('scheduledTasksDesktopCreateUnsupported'),
});

/**
 * 把 relay 请求包成与 window.electron.scheduledTasks 相同的返回形状。
 *
 * 上层每个方法因此不必分叉两套写法；桌面端做不到的能力（新建、编辑、清空历史）
 * 直接缺席或返回 success:false，正好落在既有的错误分支上。
 */
const relayScheduledTaskApi = (clientId: string): ScheduledTasksSource => ({
  list: async () => {
    const res = await relayService.listScheduledTasks(clientId);
    return { success: true, tasks: res?.tasks ?? [] };
  },
  get: async (id: string) => {
    const res = await relayService.getScheduledTask(clientId, id);
    return { success: true, task: res?.task ?? null };
  },
  create: desktopUnsupported,
  update: desktopUnsupported,
  delete: async (id: string) => {
    await relayService.deleteScheduledTask(clientId, id);
    return { success: true };
  },
  toggle: async (id: string, enabled: boolean) => {
    const res = await relayService.toggleScheduledTask(clientId, id, enabled);
    return { success: true, task: res?.task ?? null, warning: res?.warning ?? null };
  },
  runManually: async (id: string) => {
    await relayService.runScheduledTaskNow(clientId, id);
    return { success: true };
  },
  stop: async (id: string) => {
    await relayService.stopScheduledTask(clientId, id);
    return { success: true };
  },
  listRuns: async (taskId: string, limit?: number, offset?: number) => {
    const res = await relayService.listScheduledTaskRuns(clientId, taskId, limit, offset);
    return { success: true, runs: res?.runs ?? [] };
  },
  listAllRuns: async (limit?: number, offset?: number) => {
    const res = await relayService.listAllScheduledTaskRuns(clientId, limit, offset);
    return { success: true, runs: res?.runs ?? [] };
  },
});

class ScheduledTaskService {
  private cleanupFns: (() => void)[] = [];
  private initialized = false;
  /** 非 null 时所有读写改走 relay，落到该桌面设备本机的定时任务上。 */
  private targetClientId: string | null = null;

  /**
   * 切换定时任务的来源环境。null = 云端（服务端 REST）。
   *
   * 切换时先清空列表：两个环境的任务 id 各自独立，残留一条上一个环境的任务，
   * 点开就会对着另一台机器发请求。
   */
  setTargetClient(clientId: string | null): void {
    if (this.targetClientId === clientId) return;
    this.targetClientId = clientId;
    store.dispatch(setTasks([]));
    store.dispatch(setAllRuns([]));
    store.dispatch(setError(null));
    void this.loadTasks();
  }

  get targetDesktopClientId(): string | null {
    return this.targetClientId;
  }

  private api(): ScheduledTasksSource | null {
    if (this.targetClientId) return relayScheduledTaskApi(this.targetClientId);
    return window.electron?.scheduledTasks ?? null;
  }

  async init(): Promise<void> {
    if (this.initialized) return;
    this.initialized = true;

    this.setupListeners();
    // App 启动时 init() 在登录之前就会跑到这里。未登录直接打 /api/scheduled-tasks 必然 401
    // （E2E 在登录页就抓到该 401），纯噪音；定时任务页面挂载时会自行 loadTasks()，登录后
    // 不缺数据。只在已有 token 时预加载。
    if (typeof localStorage !== 'undefined' && localStorage.getItem('boclaw_token')) {
      await this.loadTasks();
    }
  }

  destroy(): void {
    this.cleanupFns.forEach((fn) => fn());
    this.cleanupFns = [];
    this.initialized = false;
  }

  private setupListeners(): void {
    // 只订阅云端的推送。桌面设备的任务状态没有 relay 事件通道，靠切换/进页面时
    // 重新拉取，不在这里假装能收到实时更新。
    const api = window.electron?.scheduledTasks;
    if (!api) return;

    const cleanupStatus = api.onStatusUpdate(
      (event: ScheduledTaskStatusEvent) => {
        store.dispatch(
          updateTaskState({
            taskId: event.taskId,
            taskState: event.state,
          })
        );
      }
    );
    this.cleanupFns.push(cleanupStatus);

    const cleanupRun = api.onRunUpdate(
      (event: ScheduledTaskRunEvent) => {
        store.dispatch(addOrUpdateRun(event.run));
      }
    );
    this.cleanupFns.push(cleanupRun);
    const cleanupNotification = window.electron.ipcRenderer?.on('scheduledTask.notification', (event: { success?: boolean }) => {
      if (event?.success === false) window.dispatchEvent(new CustomEvent('app:showToast', { detail: i18nService.t('scheduledTasksNotificationFailed') }));
    });
    if (cleanupNotification) this.cleanupFns.push(cleanupNotification);
  }

  async loadTasks(): Promise<void> {
    const api = this.api();
    if (!api) return;

    store.dispatch(setLoading(true));
    try {
      const result = await api.list();
      if (result.success && result.tasks) {
        store.dispatch(setTasks(result.tasks));
      }
    } catch (err: unknown) {
      store.dispatch(setError(err instanceof Error ? err.message : String(err)));
    }
  }

  async createTask(input: ScheduledTaskInput): Promise<void> {
    const api = this.api();
    if (!api) return;

    try {
      const result = await api.create(input);
      if (result.success && result.task) {
        store.dispatch(addTask(result.task));
      } else {
        throw new Error(result.error || 'Failed to create task');
      }
    } catch (err: unknown) {
      store.dispatch(setError(err instanceof Error ? err.message : String(err)));
      throw err;
    }
  }

  async updateTaskById(
    id: string,
    input: Partial<ScheduledTaskInput>
  ): Promise<void> {
    const api = this.api();
    if (!api) return;

    try {
      const result = await api.update(id, input);
      if (result.success && result.task) {
        store.dispatch(updateTask(result.task));
      }
    } catch (err: unknown) {
      store.dispatch(setError(err instanceof Error ? err.message : String(err)));
      throw err;
    }
  }

  async deleteTask(id: string): Promise<void> {
    const api = this.api();
    if (!api) return;

    try {
      const result = await api.delete(id);
      if (result.success) {
        store.dispatch(removeTask(id));
      }
    } catch (err: unknown) {
      store.dispatch(setError(err instanceof Error ? err.message : String(err)));
      throw err;
    }
  }

  async toggleTask(id: string, enabled: boolean): Promise<string | null> {
    const api = this.api();
    if (!api) return null;

    try {
      const result = await api.toggle(id, enabled);
      if (result.success && result.task) {
        store.dispatch(updateTask(result.task));
      }
      return result.warning ?? null;
    } catch (err: unknown) {
      store.dispatch(setError(err instanceof Error ? err.message : String(err)));
      throw err;
    }
  }

  async runManually(id: string): Promise<void> {
    const api = this.api();
    if (!api) return;

    try {
      await api.runManually(id);
    } catch (err: unknown) {
      store.dispatch(setError(err instanceof Error ? err.message : String(err)));
      throw err;
    }
  }

  async stopTask(id: string): Promise<void> {
    const api = this.api();
    if (!api) return;

    try {
      await api.stop(id);
    } catch (err: unknown) {
      store.dispatch(setError(err instanceof Error ? err.message : String(err)));
      throw err;
    }
  }

  async loadRuns(taskId: string, limit?: number, offset?: number): Promise<void> {
    const api = this.api();
    if (!api) return;

    try {
      const result = await api.listRuns(taskId, limit, offset);
      if (result.success && result.runs) {
        store.dispatch(setRuns({ taskId, runs: result.runs }));
      }
    } catch (err: unknown) {
      store.dispatch(setError(err instanceof Error ? err.message : String(err)));
    }
  }

  async clearRunHistory(): Promise<number> {
    const api = this.api();
    if (!api?.clearRunHistory) {
      throw new Error('Run history cleanup is not supported');
    }

    try {
      const result = await api.clearRunHistory();
      if (!result.success) {
        throw new Error(result.error || 'Failed to clear run history');
      }
      return result.deleted ?? 0;
    } catch (err: unknown) {
      store.dispatch(setError(err instanceof Error ? err.message : String(err)));
      throw err;
    }
  }

  async loadAllRuns(limit?: number, offset?: number): Promise<void> {
    const api = this.api();
    if (!api) return;

    try {
      const result = await api.listAllRuns(limit, offset);
      if (result.success && result.runs) {
        if (offset && offset > 0) {
          store.dispatch(appendAllRuns(result.runs));
        } else {
          store.dispatch(setAllRuns(result.runs));
        }
      }
    } catch (err: unknown) {
      store.dispatch(setError(err instanceof Error ? err.message : String(err)));
    }
  }
}

export const scheduledTaskService = new ScheduledTaskService();
